<script lang="ts">
  import { push } from "svelte-spa-router";
  import { login, ApiError, SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_FLAG } from "../lib/api";
  import { get } from "svelte/store";
  import { setUser, currentUser } from "../lib/stores/auth";
  import { resetFilters } from "../lib/filters";

  // The previous user's filters stay in memory after sign-out; clear them as
  // soon as the sign-in page opens, so Back to the map cannot reuse them.
  // Sign-out clears the user before coming here. A signed-in user can also
  // land here (Back from the map to the bare root), and keeps their filters.
  if (!get(currentUser)) resetFilters();

  let username = "";
  let password = "";
  let errorMsg = "";
  let fieldError = "";
  let submitting = false;
  let showForgot = false;

  // Set by handleSessionExpired() when a request 401s mid-session. Read once
  // and cleared, so a later visit to this page doesn't repeat it.
  let expiredMsg = "";
  try {
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
      expiredMsg = SESSION_EXPIRED_MESSAGE;
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    }
  } catch { /* storage blocked: no message, sign-in still works */ }

  async function handleSubmit() {
    fieldError = "";
    errorMsg = "";
    // Usernames never contain whitespace; phone autocomplete often appends a space.
    // The server strips it too. The password is sent exactly as typed.
    const name = username.trim();
    if (!name) { fieldError = "Username is required"; return; }
    if (!password) { fieldError = "Password is required"; return; }
    submitting = true;
    try {
      const user = await login(name, password);
      setUser(user);
      push("/map");
    } catch (e) {
      if (e instanceof ApiError) {
        errorMsg = "Those credentials didn't match.";
      } else {
        errorMsg = "Unexpected error. Please try again.";
      }
    } finally {
      submitting = false;
    }
  }
</script>

<main class="page">
  <aside class="hero">
    <div class="hero-inner">
      <span class="eyebrow">North Carolina · House District 51</span>
      <h1 class="hero-title">
        Canvass<span class="ampersand">.</span>
      </h1>
      <p class="hero-sub">Field tool for the April Montgomery campaign.</p>

      <!-- Hand-drawn "hills + sun" mark, echoing the campaign logo. -->
      <svg class="mark" viewBox="0 0 240 100" aria-hidden="true">
        <defs>
          <linearGradient id="sun-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#F2C457"/>
            <stop offset="100%" stop-color="#C99326"/>
          </linearGradient>
        </defs>
        <circle cx="186" cy="46" r="14" fill="url(#sun-g)"/>
        <!-- Far hill -->
        <path d="M0 78 Q 50 50 100 64 T 200 60 L 240 70 L 240 100 L 0 100 Z"
              fill="#2D7A4F" opacity="0.55"/>
        <!-- Near hill -->
        <path d="M0 92 Q 60 64 130 82 T 240 86 L 240 100 L 0 100 Z"
              fill="#1F5135"/>
      </svg>

      <p class="hero-quote">
        <span class="quote-mark">“</span>Responsive Leadership.
        <em>Real Representation.</em><span class="quote-mark">”</span>
      </p>
    </div>
    <div class="hero-foot">
      <span class="mono">canvass.v1</span>
      <span>·</span>
      <span>private system</span>
    </div>
  </aside>

  <section class="form-side">
    <form class="form-card" on:submit|preventDefault={handleSubmit} autocomplete="on">
      <header class="form-head">
        <span class="eyebrow">Sign in</span>
        <h2>Welcome back.</h2>
        <p class="form-sub">Volunteer credentials only. Voter data is private and logged.</p>
      </header>

      {#if expiredMsg && !errorMsg}
        <p role="status" class="msg msg-field">{expiredMsg}</p>
      {/if}
      {#if errorMsg}
        <p role="alert" class="msg msg-error">{errorMsg}</p>
      {/if}
      {#if fieldError}
        <p class="msg msg-field">{fieldError}</p>
      {/if}

      <label class="field">
        <span class="field-label">Username</span>
        <input
          type="text"
          bind:value={username}
          autocomplete="username"
          autocapitalize="off"
          spellcheck="false"
        />
      </label>

      <label class="field">
        <div class="field-label-row">
          <span class="field-label">Password</span>
          <button
            type="button"
            class="forgot-link"
            on:click={() => (showForgot = true)}
          >Forgot password?</button>
        </div>
        <input
          type="password"
          bind:value={password}
          autocomplete="current-password"
        />
      </label>

      <button type="submit" class="submit" disabled={submitting}>
        {#if submitting}
          <span class="spinner" aria-hidden="true"></span>
          Signing in…
        {:else}
          Sign in →
        {/if}
      </button>

      <p class="legal">
        Don't have an account? Ask Lucas Coleman for an invite link.
      </p>
    </form>
  </section>

  {#if showForgot}
    <button class="forgot-backdrop" on:click={() => (showForgot = false)} aria-label="Close" />
    <section class="forgot-modal" role="dialog" aria-modal="true" aria-label="Forgot password">
      <header class="forgot-head">
        <span class="eyebrow">Account recovery</span>
        <h2>Forgot your password?</h2>
        <button class="forgot-close" on:click={() => (showForgot = false)} aria-label="Close">✕</button>
      </header>
      <p class="forgot-body">
        Voter records are private, so password resets are handled by hand
        instead of via email. Reach out to Lucas Coleman and he'll send
        you a single-use reset link.
      </p>
      <p class="forgot-hint">
        Include your username so he can find your account.
      </p>
      <div class="forgot-actions">
        <button class="forgot-done" on:click={() => (showForgot = false)}>Got it</button>
      </div>
    </section>
  {/if}
</main>

<style>
  .page {
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    min-height: 100dvh;
    background: var(--paper);
    color: var(--ink);
    overflow: hidden;
  }
  /* Subtle paper grain + ruled-page lines on the hero side */
  .hero {
    position: relative;
    background: var(--leaf);
    color: var(--paper);
    padding: var(--s-7) var(--s-7);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    isolation: isolate;
  }
  .hero::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(circle at 78% 22%, rgba(229, 178, 64, .22) 0%, transparent 38%),
      repeating-linear-gradient(0deg, transparent 0 31px, rgba(255, 255, 255, .04) 31px 32px);
    pointer-events: none;
    z-index: 0;
  }
  .hero::after {
    /* Right margin "rule" reminiscent of a notebook page */
    content: '';
    position: absolute; top: 0; bottom: 0; right: 36px;
    width: 1px; background: rgba(229, 178, 64, .35);
    z-index: 0;
  }
  .hero-inner, .hero-foot {
    position: relative;
    z-index: 1;
  }
  .hero-inner {
    max-width: 520px;
  }
  .eyebrow {
    color: var(--sun-bright);
    display: inline-block;
    margin-bottom: var(--s-5);
  }
  .hero-title {
    font-family: var(--font-display);
    font-size: clamp(3rem, 9vw, 5.5rem);
    line-height: 0.95;
    font-weight: 500;
    font-variation-settings: "opsz" 120, "SOFT" 30;
    margin: 0 0 var(--s-4);
    color: var(--paper);
  }
  .ampersand {
    color: var(--sun-bright);
    font-style: italic;
  }
  .hero-sub {
    font-size: 1.1rem;
    line-height: 1.5;
    color: rgba(247, 243, 234, 0.78);
    margin: 0 0 var(--s-6);
    max-width: 36ch;
  }
  .mark {
    width: 280px;
    max-width: 100%;
    height: auto;
    margin: var(--s-6) 0 var(--s-5) -8px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.18));
  }
  .hero-quote {
    font-family: var(--font-display);
    font-style: italic;
    font-size: 1.25rem;
    line-height: 1.4;
    color: rgba(247, 243, 234, 0.92);
    max-width: 28ch;
    margin: 0;
  }
  .hero-quote em {
    color: var(--sun-bright);
    font-style: italic;
  }
  .quote-mark {
    color: var(--sun-bright);
    font-size: 1.6em;
    line-height: 0;
    vertical-align: -0.18em;
  }
  .hero-foot {
    display: flex;
    gap: var(--s-3);
    align-items: baseline;
    color: rgba(247, 243, 234, 0.55);
    font-size: 0.78rem;
    letter-spacing: 0.05em;
  }

  /* Form side */
  .form-side {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--s-7) var(--s-5);
    background: var(--paper);
  }
  .form-card {
    width: 100%;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    gap: var(--s-4);
  }
  .form-head {
    margin-bottom: var(--s-3);
  }
  .form-head .eyebrow {
    color: var(--leaf-bright);
    display: block;
    margin-bottom: var(--s-2);
  }
  .form-head h2 {
    font-size: 2.2rem;
    line-height: 1.1;
    font-weight: 600;
    margin: 0 0 var(--s-3);
    color: var(--ink);
  }
  .form-sub {
    color: var(--ink-soft);
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 36ch;
    margin: 0;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
  }
  .field-label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    color: var(--ink-soft);
  }
  .field input {
    font-family: var(--font-body);
    font-size: 1rem;
    padding: 14px 16px;
    background: var(--field);
    border: 1px solid var(--rule);
    border-radius: 0;
    color: var(--ink);
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
    border-bottom: 2px solid var(--rule-strong);
  }
  .field input:hover {
    border-color: var(--rule-strong);
  }
  .field input:focus {
    outline: none;
    border-color: var(--leaf-bright);
    border-bottom-color: var(--leaf-bright);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(45, 122, 79, 0.12);
  }
  .submit {
    position: relative;
    margin-top: var(--s-3);
    padding: 14px 20px;
    background: var(--ink);
    color: var(--paper);
    border: none;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--s-2);
    transition: background 0.15s ease, transform 0.05s ease;
  }
  .submit:hover:not(:disabled) {
    background: var(--leaf);
  }
  .submit:active:not(:disabled) {
    transform: translateY(1px);
  }
  .submit:disabled {
    opacity: 0.6;
    cursor: progress;
  }
  .spinner {
    width: 12px; height: 12px;
    border: 2px solid rgba(247, 243, 234, 0.3);
    border-top-color: var(--paper);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .msg {
    margin: 0;
    padding: 12px 14px;
    font-size: 0.9rem;
    border-left: 3px solid;
  }
  .msg-error {
    background: var(--alert-soft);
    border-color: var(--alert);
    color: var(--alert);
  }
  .msg-field {
    background: var(--sun-soft);
    border-color: var(--sun);
    color: var(--ink);
  }
  .legal {
    font-size: 0.8rem;
    color: var(--ink-muted);
    line-height: 1.5;
    margin: var(--s-4) 0 0;
    padding-top: var(--s-4);
    border-top: 1px dashed var(--rule);
  }

  /* Single-column on small screens */
  .field-label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--s-3);
  }
  .forgot-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--leaf-bright);
    font-family: var(--font-body);
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    transition: color 0.12s;
  }
  .forgot-link:hover { color: var(--leaf); }

  /* Forgot-password modal */
  .forgot-backdrop {
    position: fixed; inset: 0;
    background: rgba(15, 33, 27, 0.5);
    z-index: 1100; border: none; cursor: default;
  }
  .forgot-modal {
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--paper);
    border: 1px solid var(--rule);
    box-shadow: 0 18px 40px rgba(15, 33, 27, .25);
    z-index: 1101;
    padding: var(--s-5);
    color: var(--ink);
  }
  .forgot-head {
    position: relative;
    margin-bottom: var(--s-4);
  }
  .forgot-head .eyebrow {
    color: var(--leaf-bright);
    display: block;
    margin-bottom: 4px;
  }
  .forgot-head h2 {
    font-family: var(--font-display);
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0;
  }
  .forgot-close {
    position: absolute;
    top: -6px;
    right: -6px;
    background: transparent; border: none;
    font-size: 1.2rem; cursor: pointer;
    color: var(--ink-soft);
    padding: 4px 10px;
    min-width: 36px; min-height: 36px;
  }
  .forgot-close:hover { color: var(--ink); }
  .forgot-body {
    margin: 0 0 var(--s-4);
    color: var(--ink-soft);
    font-size: 0.95rem;
    line-height: 1.55;
  }
  .forgot-hint {
    margin: 0 0 var(--s-4);
    padding: var(--s-3) var(--s-4);
    background: var(--leaf-soft);
    border-left: 3px solid var(--leaf-bright);
    font-size: 0.85rem;
    color: var(--leaf-ink);
    line-height: 1.5;
  }
  .forgot-actions {
    display: flex; justify-content: flex-end;
  }
  .forgot-done {
    font-family: var(--font-body);
    padding: 9px 18px;
    background: var(--ink);
    color: var(--paper);
    border: 1px solid var(--ink);
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }
  .forgot-done:hover { background: var(--leaf); border-color: var(--leaf); }

  @media (max-width: 880px) {
    .page {
      grid-template-columns: 1fr;
    }
    .hero {
      padding: var(--s-6) var(--s-5);
      min-height: 360px;
    }
    .hero-title {
      font-size: clamp(2.5rem, 12vw, 4rem);
    }
    .mark { width: 220px; }
    .form-side {
      padding: var(--s-6) var(--s-4);
    }
  }
  @media (max-width: 500px) {
    .hero { min-height: auto; padding: var(--s-5) var(--s-4); }
    .mark { display: none; }
    .hero-quote { font-size: 1.05rem; }
  }
</style>
